import { ParticlesOptimisedWebGL } from 'app/views/home/webgl/particlesOptimised/ParticlesOptimisedWebGL';
import { GpuSystemsDepartment } from 'app/views/home/webgl/shadersWebGL/GpuSystemsDepartment';
import { shadersSystemsConfig } from 'app/views/home/webgl/shadersWebGL/shadersSystemsConfig';

import type { DepartmentDeps, SystemsDepartmentLike } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';
import type { HomeWebGLType } from 'app/views/home/webgl/webgl.types';

/**
 * ShadersWebGL — the 'shaders' home WebGL type (?webgl=shaders). Design §8
 * per-phase backend selection, Stage 1 (docs/DESIGN-shaders-type.md §9).
 *
 * It is the SoA type for every CPU department (intro / icon / screens / spaces
 * run the SAME phase files, kernels and renderers, byte-for-byte — this class
 * adds NOTHING to them) and moves ONLY the systems department onto the GPU. It
 * reuses ParticlesOptimisedWebGL wholesale and substitutes a GpuSystemsDepartment
 * for the CPU SystemsDepartment through two minimal, non-behavioural seams the
 * base exposes:
 *
 *   - `_useGpuSystems()` -> true: the base skips mounting the CPU systems pool
 *     renderer (so no per-particle systems upload, no systems kernels run) —
 *     the GPU department owns systems entirely, including its own render.
 *   - `_createSystemsDepartment()` -> the GPU department (still a
 *     DepartmentScene, so the facade's switcher / passive-tick / show-from-work
 *     routing is unchanged).
 *
 * Everything else — the preload handshake, the DepartmentSwitcher, the work
 * transitions, freeze/resize/destroy — is inherited unchanged. If the WebGL2
 * float-texture path is unavailable (weak GPU, design §10.1) the GPU department
 * simply renders no systems field; the rest of the type is unaffected.
 */
export class ShadersWebGL extends ParticlesOptimisedWebGL {
	public readonly type: HomeWebGLType = 'shaders';

	protected _useGpuSystems(): boolean {
		return true;
	}

	protected _createSystemsDepartment(deps: DepartmentDeps): SystemsDepartmentLike {
		return new GpuSystemsDepartment(deps, shadersSystemsConfig);
	}
}
